import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../lib/auth.context'
import { isPublicPath } from '../../lib/route-protection'
import { WrongPortalNotice } from '../wrong-portal-notice'

interface ProtectedRouteProps {
  children: React.ReactNode
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { authStatus, wrongPortal, wrongPortalRedirecting, isBanned, bannedUntil } = useAuth()
  const location = useLocation()

  // FF-3636: the portal-membership gate in auth.context has already signed this user out. Explain
  // before anything else, otherwise they land on a login screen with no reason for it.
  if (wrongPortal) {
    return <WrongPortalNotice membership={wrongPortal} redirecting={wrongPortalRedirecting} />
  }

  if (isBanned && bannedUntil) {
    const BannedUserMessage = React.lazy(() =>
      import('../admin/users/banned-user-message').then((m) => ({ default: m.BannedUserMessage })),
    )
    return (
      <React.Suspense fallback={<div>Loading...</div>}>
        <BannedUserMessage bannedUntil={bannedUntil} />
      </React.Suspense>
    )
  }

  if (isPublicPath(location.pathname.toLowerCase())) {
    return <>{children}</>
  }

  // FF-3636: hold while the initial getSession() and profile load are still in flight. Without
  // this, enabling the guard would bounce every restored session to login on first paint.
  if (authStatus === 'loading') {
    return <div data-testid="auth-loading" />
  }

  if (authStatus === 'unauthenticated') {
    return <Navigate to="/auth/login" state={{ from: location }} replace />
  }

  // FF-3636: keyed on the session's assurance level, not the isTOTPVerified flag, which is reset
  // on every auth state change and so is always false for a restored session.
  if (authStatus === 'mfa_required' && !location.pathname.toLowerCase().startsWith('/auth/')) {
    console.log('[ProtectedRoute] TOTP not verified for this session, redirecting to login')
    return <Navigate to="/auth/login" state={{ from: location }} replace />
  }

  return <>{children}</>
}
